import type { Journey, JourneyFolder } from "./gql"

export type YourJourneysListItem =
  | { type: "journey"; journey: Journey }
  | { type: "folder"; folder: JourneyFolder }

export type BoxEdges = { isStart: boolean; isEnd: boolean }

/**
 * Lists folders and the journeys outside them in the order Manager arranged
 * them: both share one position sequence. A folder goes first on a tie, as
 * its journeys come right after it.
 */
export const getJourneyListItems = (
  folders: JourneyFolder[],
  journeysOutsideFolders: Journey[]
): YourJourneysListItem[] => {
  const folderItems = folders
    // The back end only lists folders with visible journeys, but an empty one has nothing to open
    .filter((folder) => folder.journeys.nodes.length > 0)
    .map((folder): YourJourneysListItem => ({ type: "folder", folder }))

  const journeyItems = journeysOutsideFolders.map(
    (journey): YourJourneysListItem => ({ type: "journey", journey })
  )

  return [...folderItems, ...journeyItems].sort(
    (item, otherItem) =>
      getPosition(item) - getPosition(otherItem) ||
      getTypeOrder(item) - getTypeOrder(otherItem)
  )
}

export const getFolderJourneys = (folder: JourneyFolder) =>
  folder.journeys.nodes

/**
 * The journey to open when the folder is selected: the first one not
 * completed yet that the user can start. If all of those are locked, the
 * first of them still shows what is blocking. If all are completed, the first.
 */
export const getFolderStartJourney = (folder: JourneyFolder) => {
  const journeys = getFolderJourneys(folder)
  const unfinishedJourneys = journeys.filter(
    (journey) => !getIsJourneyCompleted(journey)
  )

  return (
    unfinishedJourneys.find((journey) => !journey.isLocked) ??
    unfinishedJourneys[0] ??
    journeys[0]
  )
}

/**
 * A journey can be both certified and locked. Locked wins, so it shows its
 * lock rather than a check and doesn't count as done.
 */
export const getIsJourneyCompleted = (
  journey: Pick<Journey, "isLocked" | "progress">
) => journey.progress.certified && !journey.isLocked

export const getIsJourneyListItemCompleted = (item: YourJourneysListItem) => {
  if (item.type === "journey") return getIsJourneyCompleted(item.journey)

  return getFolderJourneys(item.folder).every(getIsJourneyCompleted)
}

/** The item showing the journey: the journey itself, or the folder holding it */
export const findJourneyListItem = (
  items: YourJourneysListItem[],
  journeyId: string | undefined
) =>
  items.find((item) => {
    if (item.type === "journey") return item.journey.id === journeyId

    return getFolderJourneys(item.folder).some(
      (journey) => journey.id === journeyId
    )
  })

export const getJourneyListItemKey = (item: YourJourneysListItem) =>
  item.type === "journey"
    ? `journey-${item.journey.id}`
    : `folder-${item.folder.id}`

const getPosition = (item: YourJourneysListItem) =>
  item.type === "journey" ? item.journey.position : item.folder.position

const getTypeOrder = (item: YourJourneysListItem) =>
  item.type === "folder" ? 0 : 1

/**
 * Where each item sits in a box shared by consecutive items that pass
 * `hasBox`. Items without a box get null.
 */
export const getBoxEdges = (
  items: YourJourneysListItem[],
  hasBox: (item: YourJourneysListItem) => boolean
): (BoxEdges | null)[] =>
  items.map((item, index) => {
    if (!hasBox(item)) return null

    const previousItem = items[index - 1]
    const nextItem = items[index + 1]

    return {
      isStart: !previousItem || !hasBox(previousItem),
      isEnd: !nextItem || !hasBox(nextItem),
    }
  })
